/**
 * Subscription Context for EASI 2.0
 *
 * Fetches subscription info on auth and provides it app-wide.
 * Handles: token balance, subscription status, group membership.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useAuth } from '../hooks/useAuth';
import { subscription as subscriptionApi, type SubscriptionInfo } from '../api/endpoints';

interface SubscriptionState {
  subscription: SubscriptionInfo | null;
  isLoading: boolean;
  error: string | null;
  /** Whether the user has any active subscription */
  hasSubscription: boolean;
  /** Whether the user has admin-granted full access (bypasses subscription) */
  hasFullAccess: boolean;
  /** Whether the subscription has expired */
  isExpired: boolean;
  /** Whether the user has tokens remaining */
  hasTokens: boolean;
  /** Tokens remaining (individual or group) */
  tokensRemaining: number;
  /** Whether the user is a group leader */
  isGroupLeader: boolean;
  /** Whether the user is in a group (leader OR invited member) */
  isGroupMember: boolean;
  /** Group ID if the user is in a group */
  groupId: string | null;
  /** Whether this is a monthly (30-day) plan */
  isMonthly: boolean;
  /** Refresh subscription data (e.g., after token consumption) */
  refresh: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionState | undefined>(undefined);

interface SubscriptionProviderProps {
  children: ReactNode;
}

export function SubscriptionProvider({ children }: SubscriptionProviderProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscription = useCallback(async (silent = false) => {
    if (!isAuthenticated) {
      setSubscription(null);
      return;
    }

    // Only show loading screen on initial fetch (not tab-return re-fetches),
    // so the App-level paywall gate doesn't unmount the component tree and
    // destroy in-progress workflow state.
    if (!silent) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const response = await subscriptionApi.getMySubscription();
      setSubscription(response.data);
    } catch (err: any) {
      const code = err?.code || '';
      // Positive no-subscription signal — the API confirmed this user has no row.
      if (code === 'HTTP_404' || code === 'NOT_FOUND') {
        setSubscription({
          hasSubscription: false,
          hasFullAccess: false,
          userId: '',
          email: '',
          plan: '',
          status: 'none',
          purchaseDate: '',
          expirationDate: '',
          daysRemaining: 0,
          isExpired: false,
          tokensAllocated: 0,
          tokensUsed: 0,
          tokensRemaining: 0,
          isGroupMember: false,
          groupId: null,
          isGroupLeader: false,
          renewUrl: 'https://www.northernspeech.com/4a03/',
        });
        return;
      }
      // Any other failure (HTTP_401, HTTP_5xx, NETWORK_ERROR, REQUEST_ERROR, etc.):
      // preserve the last-known-good subscription. Do NOT set hasSubscription=false.
      // The App.tsx paywall gate renders only on a positive no-subscription signal
      // (subscription !== null && !subscription.hasSubscription); transient errors
      // surface via the error field as a recoverable retry banner.
      setError(err?.message || 'Unable to load subscription information.');
      console.error('[SubscriptionContext] non-404 fetch error', code, err?.message);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch on auth change + refresh when tab becomes visible
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchSubscription();
      // Refresh subscription on tab focus (e.g. after admin grants tokens).
      // Silent=true so isLoading stays false — prevents App paywall gate
      // from unmounting the component tree and destroying workflow state.
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          fetchSubscription(true);
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    } else if (!authLoading && !isAuthenticated) {
      setSubscription(null);
    }
  }, [authLoading, isAuthenticated, fetchSubscription]);

  const value = useMemo<SubscriptionState>(() => {
    const sub = subscription;
    const hasSubscription = sub?.hasSubscription ?? false;
    const hasFullAccess = sub?.hasFullAccess ?? false;
    const isExpired = sub?.isExpired ?? false;
    const isGroupMember = sub?.isGroupMember ?? false;

    // For group members, use group token balance
    let tokensRemaining = sub?.tokensRemaining ?? 0;
    if (isGroupMember && sub?.groupTokensRemaining !== undefined) {
      tokensRemaining = sub.groupTokensRemaining;
    }

    return {
      subscription: sub,
      isLoading,
      error,
      hasSubscription: hasSubscription || hasFullAccess,
      hasFullAccess,
      isExpired: hasSubscription && isExpired && !hasFullAccess,
      hasTokens: hasFullAccess || tokensRemaining > 0,
      tokensRemaining,
      isGroupLeader: sub?.isGroupLeader ?? false,
      isGroupMember,
      groupId: sub?.groupId ?? null,
      isMonthly: sub?.plan === 'monthly_30',
      // Always silent — subscription data already exists when refresh() is called.
      // Prevents isLoading=true from triggering App paywall gate unmount.
      refresh: () => fetchSubscription(true),
    };
  }, [subscription, isLoading, error, fetchSubscription]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription(): SubscriptionState {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}

export default SubscriptionContext;
